import { useCallback, useState, type ReactNode, type Ref, forwardRef } from 'react';
import NumberFormat, { type NumberFormatValues } from 'react-number-format';
import { useIntl } from 'react-intl';
import { isNil } from 'lodash';
import { clampToRange, type INumberFormatSeparators } from './numbers';
import valueProp from './valueProp';
import s from './NumberInputSimple.module.scss';

interface INumberInputSimpleProps {
  name?: string;
  label?: ReactNode;
  defaultValue?: number | null;
  disabled?: boolean;
  max?: number;
  min?: number;
  required?: boolean;
  thousandSeparator?: string | boolean;
}

const config: Record<string, INumberFormatSeparators> = {
  en: {
    thousandSeparator: ',',
    decimalSeparator: '.',
  },
  de: {
    thousandSeparator: '.',
    decimalSeparator: ',',
  },
};

const NumberInputSimple = (
  {
    name,
    label,
    defaultValue,
    disabled = false,
    max = 5000000,
    min,
    required = false,
    thousandSeparator,
  }: INumberInputSimpleProps,
  ref: Ref<NumberFormat>,
) => {
  const [value, setValue] = useState<number | null | undefined>(defaultValue);
  const { locale } = useIntl();
  const [, forceRerender] = useState(false);

  const handleChange = useCallback(
    (values: NumberFormatValues) => {
      let v;
      if (isNil(values.floatValue)) {
        v = null;
      } else {
        v = clampToRange(values.floatValue, min, max);
      }
      setValue(v);
      forceRerender((b) => !b);
    },
    [max, min],
  );

  return (
    <div className={s.fieldWrapper}>
      <NumberFormat
        allowNegative={false}
        allowLeadingZeros={false}
        allowEmptyFormatting={false}
        {...(!isNil(thousandSeparator) ? { thousandSeparator } : config[locale])}
        name={name}
        {...valueProp(value)}
        onValueChange={handleChange}
        autoComplete="off"
        id={name}
        ref={ref}
        onFocus={(e) => e.target.focus()}
        required={required}
        className={s.numberInput}
        disabled={disabled}
        type="text"
        max={max}
      />
      <label className="text-label" htmlFor={name} id={`label-${name}`}>
        {label}
      </label>
    </div>
  );
};

export default forwardRef(NumberInputSimple);
